import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form';
import { isEmpty, maxBy } from 'lodash';
import { Box, Grid } from '@material-ui/core';
import { getCityVillages, selectSortedCityVillages, selectSortedCountries, selectSortedRAStates } from '../../../../redux/modules/countries';
import { updatePropertyForm } from '../../../../redux/modules/propertyClaim';
import ClaimStepNavigationWrapper from '../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import AccidentInformation from '../../components/AccidentInformation';
import LoanExisting from '../../components/LoanExisting';
import DamageCause from '../../components/DamageCause';
import AccidentDiscoverer from '../../components/AccidentDiscoverer';
import InformedAboutAccident from '../../components/InformedAboutAccident';
import AccidentDetails from '../../components/AccidentDetails';
import DamagedProperties from '../../components/DamagedProperties';
import TextInputFieldWithValidation from '../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import ArmenianRegionSelectOptions from '../../../../components/FormComponents/ContactInfo/ArmenianRegionSelectOptions';
import SelectWithValidation from '../../../../components/ValidationFormElements/SelectWithValidation';
import FormSection from '../../../../components/FormComponents/FormSection';
import {
  accidentInfoSchema,
  getPropertySchema,
} from '../../../../validations/claimValidationSchema/property/propertyClaimValidationSchema';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import useUnRegisterValidationFields from '../../../../hooks/useUnRegisterValidationFields';
import { bindPropertyClaimAccidentAddress } from '../../../../helpers/claims/propertyClaim';
import { findCountriesByISO3, findIsYerevan } from '../../../../helpers/countryHelper';
import { getMenuLabelWithTranslation } from '../../../../helpers/selectHelpers';
import { ACCIDENT_INFO_FIELDS, DEFAULT_DAMAGED_PROPERTIES } from '../../options';
import { ARMENIA_ISO3 } from '../../../../constants';

import AccidentPlaceIcon from '../../../../img/formSectionIcons/accidentPlace.svg';

const PropertyAccidentInfoStep = ({
  step,
  goNext,
  goPrevious,
  params: { setStepNavigationBox, formLanguage },
}) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const isDeviceLgSize = useDeviceResize('lg');

  const propertyClaim = useSelector((store) => store.propertyClaim, shallowEqual);
  const initialData = propertyClaim.accidentInfo;

  const [damagedProperties, setDamagedProperties] = useState(
    (initialData && initialData.damagedProperties) || DEFAULT_DAMAGED_PROPERTIES,
  );

  const reduceValidationSchema = (newDamagedProperties) => newDamagedProperties.reduce(
    (acc, val) => acc.concat(getPropertySchema(val.id)),
    accidentInfoSchema,
  );

  const [validationSchema, setValidationSchema] = useState(
    reduceValidationSchema(damagedProperties),
  );

  const { handleSubmit, register, unregister, setValue, errors, clearError } = useForm({
    validationSchema,
    defaultValues: initialData,
    mode: 'onBlur',
  });

  useRegisterValidationFields(ACCIDENT_INFO_FIELDS, register);
  useUnRegisterValidationFields(ACCIDENT_INFO_FIELDS, unregister);

  const sortedStates = useSelector((state) => selectSortedRAStates(state, i18n.language));
  const sortedCityVillages = useSelector((state) => selectSortedCityVillages(state, i18n.language));
  const sortedCountries = useSelector((state) => selectSortedCountries(state, i18n.language));
  const filteredCountries = [findCountriesByISO3([ARMENIA_ISO3], sortedCountries)];
  const damageCauses = useSelector((state) => state.claimApplication.damageCauses);
  const organizations = useSelector((state) => state.claimApplication.organizations);

  const [accDate, setAccDate] = useState(initialData && initialData.accidentDate);
  const [accAddress, setAccAddress] = useState(
    () => bindPropertyClaimAccidentAddress(
      initialData,
      formLanguage,
      sortedStates,
      sortedCountries,
    ),
  );
  const accIsYerevan = useMemo(() => findIsYerevan(accAddress.accState), [accAddress.accState]);

  useEffect(() => {
    if (accAddress.accState) {
      dispatch(getCityVillages(accAddress.accState.id));
    }
  }, [dispatch, accAddress.accState]);

  useEffect(() => {
    if (sortedCityVillages?.length) {
      setAccAddress({
        ...accAddress,
        accVillage: initialData.accVillage ? sortedCityVillages.find(
          (cityVillage) => String(cityVillage.masysId) === String(initialData.accVillage.masysId),
        )
          : initialData
          && initialData.realEstateLocation
          && initialData.realEstateLocation.location2Id
          && sortedCityVillages.find(
            (cityVillage) => cityVillage.masysId.toString()
              === initialData.realEstateLocation.location2Id.toString(),
          ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedCityVillages, initialData]);

  useEffect(() => {
    setValue('accState', accAddress.accState);
    setValue('accCountry', accAddress.accCountry);
    setValue('accVillage', accAddress.accVillage);
    setValue('accStreet', accAddress.accStreet);
    setValue('isAccInArmenia', accAddress.isAccInArmenia);
  }, [accAddress, setValue]);

  const [loanAgreementExists, setLoanAgreementExists] = useState(
    (initialData && initialData.loanAgreementExists) || false,
  );
  const [accidentDiscovererIs, setAccidentDiscovererIs] = useState(
    initialData && initialData.accidentDiscovererIs,
  );
  const [damageAmount, setDamageAmount] = useState(initialData && initialData.damageAmount);
  const [selectedDamageCauses, setSelectedDamageCauses] = useState(
    (initialData && initialData.damageCauses) || [],
  );
  const [selectedOrganizations, setSelectedOrganizations] = useState(
    (initialData && initialData.organizations) || [],
  );

  const [accidentDetails, setAccidentDetails] = useState(
    initialData && initialData.accidentDetails,
  );

  const [accidentPrevention, setAccidentPrevention] = useState(
    initialData && initialData.accidentPrevention,
  );

  const getLabelWithTranslation = useCallback(
    (option) => getMenuLabelWithTranslation(option, i18n.language),
    [i18n],
  );

  useEffect(() => {
    setValue('formLanguage', formLanguage);
  }, [formLanguage, setValue]);

  useEffect(() => {
    setValue('loanAgreementExists', loanAgreementExists);
  }, [loanAgreementExists, setValue]);

  useEffect(() => {
    damagedProperties.forEach((dp) => {
      register({ name: `propertyName_${dp.id}` });
      register({ name: `propertyYear_${dp.id}` });
      register({ name: `propertyPrice_${dp.id}` });
      register({ name: `damageDescription_${dp.id}` });

      setValue(`propertyName_${dp.id}`, dp.propertyName, !!dp.propertyName);
      setValue(`propertyYear_${dp.id}`, dp.propertyYear, !!dp.propertyYear);
      setValue(`propertyPrice_${dp.id}`, dp.propertyPrice, !!dp.propertyPrice);
      setValue(`damageDescription_${dp.id}`, dp.damageDescription, !!dp.damageDescription);
    });
  }, [damagedProperties, setValue, register]);

  const updateAccDate = (newDate) => {
    setAccDate(newDate);
    setValue('accidentDate', newDate, true);
  };

  const handleLoanAgreementExists = (value) => {
    setLoanAgreementExists(value);
  };

  const handleDamageAmountChange = (value) => {
    setDamageAmount(value);
    setValue('damageAmount', value, !!value);
  };

  const handleAccidentDiscovererIsChange = (value) => {
    setAccidentDiscovererIs(value);
    setValue('accidentDiscovererIs', value, true);
  };

  const handleDamageCausesChange = (value, checked) => {
    let newDamageCauses = [];
    if (checked) {
      newDamageCauses = [...selectedDamageCauses, value];
    } else {
      newDamageCauses = selectedDamageCauses.filter((dc) => dc.id !== value.id);
    }
    setSelectedDamageCauses(newDamageCauses);
    setValue('damageCauses', newDamageCauses, true);
  };

  const handleOrganizationsChange = (value, checked) => {
    let newValues = [];
    if (checked) {
      if (value.label === 'nobody') {
        newValues = [value];
      } else {
        newValues = [...selectedOrganizations.filter((org) => org.label !== 'nobody'), value];
      }
    } else {
      newValues = selectedOrganizations.filter((org) => org.id !== value.id);
    }
    setSelectedOrganizations(newValues);
    setValue('organizations', newValues, true);
  };

  const handleDamagedPropertyChange = (id, newValue) => {
    setDamagedProperties((curr) => curr && curr.map((dp) => (dp.id === id ? newValue : dp)));
  };

  const handleDamagedPropertyDelete = (id) => {
    const newDamagedProperties = damagedProperties && damagedProperties.filter(
      (property) => property.id !== id,
    );
    setDamagedProperties(newDamagedProperties);
    setValidationSchema(reduceValidationSchema(newDamagedProperties));
  };

  const handleAccidentDetailsChange = (value) => {
    const fixedValue = value.replace(/ {2,}/g, ' ');
    setAccidentDetails(fixedValue);
    setValue('accidentDetails', fixedValue, true);
  };

  const handleAccidentPreventionChange = (value) => {
    const fixedValue = value.replace(/ {2,}/g, ' ');
    setAccidentPrevention(fixedValue);
    setValue('accidentPrevention', fixedValue, true);
  };

  const addNewProperty = () => {
    const propertyWithMaxId = maxBy(damagedProperties, 'id');
    const newId = propertyWithMaxId ? propertyWithMaxId.id + 1 : 1;
    const newDamagedProperties = [...damagedProperties, { id: newId }];

    setDamagedProperties(newDamagedProperties);
    setValidationSchema(reduceValidationSchema(newDamagedProperties));
  };

  const handleAccCountryChange = (accCountry) => {
    setAccAddress({
      ...accAddress,
      accCountry,
    });
    setValue('accCountry', accCountry, true);
  };

  const handleAccStateChange = (accState) => {
    setAccAddress({
      ...accAddress,
      accState,
      accVillage: null,
      accStreet: null,
    });
    setValue('accState', accState, true);
    setValue('accVillage', null);
    setValue('accStreet', null);
  };

  const handleAccVillageChange = (accVillage) => {
    setAccAddress({
      ...accAddress,
      accVillage,
      accStreet: null,
    });
    setValue('accVillage', accVillage, true);
    setValue('accStreet', null);
  };

  const onSubmit = (data) => {
    dispatch(updatePropertyForm({ name: 'accidentInfo', data: { ...data, damagedProperties } }));
    dispatch(
      updatePropertyForm({
        name: 'attachments',
        data: { hasLoanAgreement: data.loanAgreementExists },
      }),
    );
    goNext(step);
  };

  return (
    <Box>
      <ClaimStepNavigationWrapper
        errors={errors}
        isNextDisabled={false}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        back={goPrevious}
        dependencies={[JSON.stringify(errors), isDeviceLgSize, false, damagedProperties]}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        clearError={clearError}
      />
      <AccidentInformation
        formProps={{ errors, clearError, register, setValue }}
        accDate={accDate}
        damageAmount={damageAmount}
        accidentInfo={initialData}
        updateAccDate={updateAccDate}
        updateDamageAmount={handleDamageAmountChange}
      />
      <FormSection title="claim.accInfo.accAddress" icon={AccidentPlaceIcon}>
        <Grid container spacing={4}>
          <Grid
            item
            xs={12}
            md={!isEmpty(accAddress.accState) && accAddress.isAccInArmenia && !accIsYerevan ? 4 : 6}
          >
            <SelectWithValidation
              formProps={{
                errors,
                clearError,
              }}
              value={accAddress.accCountry || ''}
              options={filteredCountries}
              getMenuLabel={getLabelWithTranslation}
              onChange={handleAccCountryChange}
              label="insurance.country"
              required
              name="accCountry"
            />
          </Grid>
          <ArmenianRegionSelectOptions
            isYerevan={accIsYerevan}
            errors={errors}
            clearError={clearError}
            cityVillages={sortedCityVillages}
            onCityVillageChange={handleAccVillageChange}
            states={sortedStates}
            onStateChange={handleAccStateChange}
            form={{
              cityVillage: {
                value: accAddress.accVillage || {},
                label: 'contactInfo.cityVillage',
                name: 'accVillage',
              },
              state: {
                value: accAddress.accState || {},
                label: 'contactInfo.state',
                name: 'accState',
              },
            }}
          />
          <Grid item xs={12}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="claim.accInfo.address"
              name="accStreet"
              handleChange={(e) => {
                setAccAddress({
                  ...accAddress,
                  accStreet: e.target.value,
                });
              }}
              register={register}
              required
            />
          </Grid>
        </Grid>
      </FormSection>
      <LoanExisting
        formProps={{ errors, clearError, register }}
        loanAgreementExists={loanAgreementExists}
        handleLoanAgreementExists={handleLoanAgreementExists}
      />
      <DamageCause
        formProps={{ errors, clearError, register }}
        i18n={i18n}
        damageCauses={damageCauses}
        selectedDamageCauses={selectedDamageCauses}
        handleDamageCausesChange={handleDamageCausesChange}
      />
      <AccidentDiscoverer
        formProps={{ errors, register, clearError }}
        i18n={i18n}
        accidentDiscovererIs={accidentDiscovererIs}
        handleAccidentDiscovererIsChange={handleAccidentDiscovererIsChange}
      />
      <InformedAboutAccident
        formProps={{ errors, clearError, register }}
        i18n={i18n}
        organizations={organizations}
        selectedOrganizations={selectedOrganizations}
        handleOrganizationsChange={handleOrganizationsChange}
      />
      <AccidentDetails
        formProps={{ errors }}
        accidentDetails={accidentDetails}
        accidentPrevention={accidentPrevention}
        handleAccidentDetailsChange={handleAccidentDetailsChange}
        handleAccidentPreventionChange={handleAccidentPreventionChange}
      />
      <DamagedProperties
        formProps={{ errors }}
        damagedProperties={damagedProperties}
        handleDamagedPropertyChange={handleDamagedPropertyChange}
        handleDamagedPropertyDelete={handleDamagedPropertyDelete}
        addNewProperty={addNewProperty}
        i18n={i18n}
      />
    </Box>
  );
};

PropertyAccidentInfoStep.propTypes = {
  step: PropTypes.shape({}).isRequired,
  goNext: PropTypes.func.isRequired,
  goPrevious: PropTypes.func.isRequired,
  params: PropTypes.shape({
    setStepNavigationBox: PropTypes.func,
    formLanguage: PropTypes.string,
  }).isRequired,
};

export default PropertyAccidentInfoStep;
